import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ClipboardCheck } from "lucide-react";

const VIEWS = [
  { k: "all", label: "All" },
  { k: "latest", label: "Latest submissions" },
  { k: "evaluate", label: "Evaluate one-ways" },
  { k: "evaluated", label: "Evaluated" },
];

const STRENGTH_STYLE = {
  Strong: { bg: "bg-teal", color: "white" },
  Moderate: { bg: "bg-amber-500", color: "white" },
  Weak: { bg: "bg-red-500", color: "white" },
};

export default function AssessmentsPage() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("all");
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => { api.get("/recruit/assessments").then(r => setRows(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (view === "latest") out = out.slice().sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || ""));
    else if (view === "evaluate") out = out.filter(r => r.status === "Submitted");
    else if (view === "evaluated") out = out.filter(r => r.status === "Evaluated");
    if (type !== "all") out = out.filter(r => r.assessment_type === type);
    if (status !== "all") out = out.filter(r => r.status === status);
    if (q) out = out.filter(r => `${r.name} ${r.email} ${r.job_title || ""}`.toLowerCase().includes(q.toLowerCase()));
    return out;
  }, [rows, view, q, type, status]);

  const counts = {
    total: rows.length,
    invited: rows.filter(r => r.status === "Invited").length,
    submitted: rows.filter(r => r.status === "Submitted").length,
    evaluated: rows.filter(r => r.status === "Evaluated").length,
  };

  return (
    <div className="space-y-6" data-testid="assessments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Recruit</div>
          <h1 className="text-3xl font-bold font-display mt-1">Assessments</h1>
          <p className="text-neutral-600 mt-1 text-sm">
            {counts.total} assessments · {counts.invited} invited · {counts.submitted} awaiting evaluation · {counts.evaluated} evaluated
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b" data-testid="assessments-views">
        {VIEWS.map(v => (
          <button key={v.k} onClick={() => setView(v.k)} data-testid={`view-${v.k}`}
            className={`px-4 py-2 text-sm rounded-t-lg -mb-px border-b-2 transition-colors ${view === v.k ? "border-pink text-pink font-semibold" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9" data-testid="assessments-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-48" data-testid="filter-type"><SelectValue placeholder="All assessment types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="One-Way">One-Way</SelectItem>
            <SelectItem value="Reference">Reference</SelectItem>
            <SelectItem value="Predictive Behavioral">Predictive Behavioral</SelectItem>
            <SelectItem value="Live">Live Video</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Invited">Invited</SelectItem>
            <SelectItem value="Submitted">Submitted</SelectItem>
            <SelectItem value="Evaluated">Evaluated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-neutral-500 bg-neutral-50">
              <tr>
                <th className="p-3">Candidate</th>
                <th className="p-3">Type</th>
                <th className="p-3">Status</th>
                <th className="p-3">Recommendation</th>
                <th className="p-3">Team avg</th>
                <th className="p-3">AI Video Review</th>
                <th className="p-3">Submitted</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const s = r.ai_strength && STRENGTH_STYLE[r.ai_strength];
                return (
                  <tr key={r.candidate_id} className="border-t hover:bg-neutral-50" data-testid={`assessment-row-${r.candidate_id}`}>
                    <td className="p-3">
                      <Link to={`/app/recruit/candidates/${r.candidate_id}`} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-navy text-white grid place-items-center text-xs font-bold">{r.name.split(" ").map(x => x[0]).join("").slice(0,2)}</div>
                        <div>
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-neutral-500 truncate">{r.job_title || "—"}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="p-3"><Badge variant="outline">{r.assessment_type}</Badge></td>
                    <td className="p-3"><Badge className={r.status === "Evaluated" ? "bg-teal text-white border-0" : ""}>{r.status}</Badge></td>
                    <td className="p-3 text-xs">{r.recommendation ? r.recommendation.replace(/_/g, " ") : "—"}</td>
                    <td className="p-3 font-tabular font-bold">{r.team_average ?? "—"}</td>
                    <td className="p-3">
                      {s ? <Badge className={`${s.bg} text-white border-0`}>{r.ai_strength}</Badge> : "—"}
                    </td>
                    <td className="p-3 text-xs text-neutral-500">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}</td>
                    <td className="p-3">
                      <Link to={`/app/recruit/candidates/${r.candidate_id}`}>
                        <span className="text-pink text-xs font-semibold hover:underline" data-testid={`evaluate-${r.candidate_id}`}>Evaluate →</span>
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-neutral-500">
                <ClipboardCheck className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
                No assessments match your filters.
              </td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
